import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";

export default function Evidence() {
  const page = webPageSchema({
    path: "/evidence",
    title: "How We Evaluate Evidence | Shariq Refai, MD, MBA",
    description:
      "The evidence hierarchy behind educational content on shariqrefai.com, from meta-analyses to expert opinion, and how guidance changes as the evidence evolves.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Evidence Methodology", path: "/evidence" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="How We Evaluate Evidence | Shariq Refai, MD, MBA"
        description="The evidence hierarchy behind educational content on shariqrefai.com, from meta-analyses to expert opinion, and how guidance changes as the evidence evolves."
        url="/evidence"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Evidence Methodology", path: "/evidence" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Evidence Methodology</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            How We Evaluate Evidence
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            The hierarchy used when writing, citing, and revising
            educational content on this site.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: May 23, 2026.</strong>
          </p>

          <p>
            shariqrefai.com publishes educational content about psychiatry,
            mental health, and human behavior. To keep that content honest,
            the editorial process leans on a structured evidence hierarchy
            rather than the loudest opinion or the most viral source.
          </p>

          <h2>The Evidence Hierarchy</h2>
          <p>
            When choosing what to cite, how strongly to frame a claim, and
            when to update an essay, sources are weighed roughly in this
            order:
          </p>
          <ol>
            <li>
              <strong>Meta-analyses</strong>: pooled quantitative analyses
              of multiple well-designed studies, when methodology is sound
              and heterogeneity is acceptable.
            </li>
            <li>
              <strong>Systematic reviews</strong>: structured, transparent
              reviews of the body of evidence on a focused clinical
              question.
            </li>
            <li>
              <strong>Practice guidelines</strong>: recommendations from
              recognized professional bodies (for example, the American
              Psychiatric Association, NICE, WHO, and similar) that
              integrate evidence with clinical judgment.
            </li>
            <li>
              <strong>Randomized controlled trials</strong>: well-designed,
              adequately powered RCTs, with attention to population,
              comparator, blinding, and outcome relevance.
            </li>
            <li>
              <strong>Cohort and observational studies</strong>: used
              cautiously, with attention to confounding and selection
              effects, especially for questions where RCTs aren't feasible.
            </li>
            <li>
              <strong>Expert opinion and clinical experience</strong> ,
              acknowledged as such. Useful for framing, illustration, and
              edge cases, but not treated as definitive on its own.
            </li>
          </ol>
          <p>
            Anecdotes, single case reports, popular media coverage, and
            social-media commentary do not constitute evidence for clinical
            claims on this site, although they may be discussed
            editorially.
          </p>

          <h2>How Claims Are Framed</h2>
          <p>
            The strength of language on this site is meant to track the
            strength of evidence. In practice:
          </p>
          <ul>
            <li>Strong, well-replicated evidence: stated plainly as what's typically observed or recommended.</li>
            <li>Mixed or emerging evidence: hedged with phrases like "current evidence suggests," "some studies indicate," or "the picture is still incomplete."</li>
            <li>Mechanism-level or theoretical discussion: clearly labeled as a model or hypothesis, not as established fact.</li>
            <li>Editorial perspective and opinion: clearly labeled as such, separate from clinical claims.</li>
          </ul>

          <h2>Why Recommendations Can Change</h2>
          <p>
            Evidence evolves. New meta-analyses come out. Guidelines are
            updated. Drug labels change. What was considered first-line
            five years ago may not be today. Content on this site is
            reviewed and revised on a recurring basis, and material updates
            are noted on the affected page.
          </p>
          <p>
            For a description of how those updates happen, see the{" "}
            <Link href="/corrections">Corrections &amp; Updates</Link>{" "}
            policy.
          </p>

          <h2>What This Site Doesn't Do</h2>
          <ul>
            <li>It doesn't provide individualized medical or psychiatric advice.</li>
            <li>It doesn't make diagnostic claims about any reader.</li>
            <li>It doesn't recommend starting, stopping, or changing any medication.</li>
            <li>It doesn't replace evaluation by a licensed clinician who knows your history.</li>
          </ul>
          <p>
            See the <Link href="/disclaimer">Medical &amp; Educational
            Disclaimer</Link> for the full position.
          </p>

          <h2>Limits of the Hierarchy</h2>
          <p>
            No hierarchy is perfect. Meta-analyses can be poorly conducted.
            Guidelines can lag. RCTs can have populations that don't look
            like real-world patients. Where these limits matter, they are
            acknowledged in the relevant content rather than papered over.
          </p>

          <h2>International Context</h2>
          <p>
            Content on this site is written primarily for a US audience.
            Practice guidelines, drug availability, regulatory bodies, and
            standards of care differ internationally. Readers outside the
            US should rely on local guidance and clinicians familiar with
            their healthcare system. See the{" "}
            <Link href="/disclaimer">disclaimer</Link> for more on
            international scope.
          </p>

          <h2>Contact</h2>
          <p>
            If you believe a claim on this site is not well supported by
            current evidence, see{" "}
            <Link href="/corrections">Corrections &amp; Updates</Link> or
            email{" "}
            <a href="mailto:corrections@shariqrefai.com">
              corrections@shariqrefai.com
            </a>{" "}
            with a link to the relevant source.
          </p>
        </div>
      </article>
      <RelatedPages
        entries={[
          { href: "/editorial-process", label: "Editorial process & standards", why: "The full authoring and review workflow." },
          { href: "/medical-reviewer", label: "Medical reviewer", why: "The clinician who signs off on clinical content." },
          { href: "/medical-review-board", label: "Medical review board", why: "Reviewer roles, credentials, and selection criteria." },
          { href: "/ai-policy", label: "AI use & editorial assistance policy", why: "Where AI does and does not appear in the workflow." },
        ]}
      />
    </SiteLayout>
  );
}
